import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuthStore } from '../stores/authStore';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

function Register() {
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);
	const navigate = useNavigate();
	const { register: registerUser, isLoading } = useAuthStore();
	
	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm();

	const password = watch('password');

	const onSubmit = async (data) => {
		const { confirmPassword, ...userData } = data;
		const result = await registerUser(userData);
		
		if (result.success) {
			toast.success('注册成功！欢迎使用 Facebook AI 广告优化系统');
			navigate('/dashboard');
		} else {
			toast.error(result.error);
		}
	};

	return (
		<div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full space-y-8">
				<div>
					<div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
						<svg
							className="h-8 w-8 text-primary-600"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
							/>
						</svg>
					</div>
					<h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">
						创建新账户
					</h2>
					<p className="mt-2 text-center text-sm text-gray-600">
						开始使用 Facebook AI 广告优化系统
					</p>
				</div>
				
				<form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
					<div className="space-y-4">
						<div>
							<label htmlFor="username" className="label">
								用户名
							</label>
							<input
								id="username"
								type="text"
								autoComplete="username"
								className={errors.username ? 'input-error' : 'input'}
								placeholder="请输入用户名"
								{...register('username', {
									required: '请输入用户名',
									minLength: {
										value: 3,
										message: '用户名至少需要3个字符'
									},
									maxLength: {
										value: 50,
										message: '用户名不能超过50个字符'
									},
									pattern: {
										value: /^[a-zA-Z0-9_-]+$/,
										message: '用户名只能包含字母、数字、下划线和连字符'
									}
								})}
							/>
							{errors.username && (
								<p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
							)}
						</div>

						<div>
							<label htmlFor="email" className="label">
								邮箱地址
							</label>
							<input
								id="email"
								type="email"
								autoComplete="email"
								className={errors.email ? 'input-error' : 'input'}
								placeholder="请输入邮箱地址"
								{...register('email', {
									required: '请输入邮箱地址',
									pattern: {
										value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
										message: '请输入有效的邮箱地址'
									}
								})}
							/>
							{errors.email && (
								<p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
							)}
						</div>

						<div>
							<label htmlFor="password" className="label">
								密码
							</label>
							<div className="relative">
								<input
									id="password"
									type={showPassword ? 'text' : 'password'}
									autoComplete="new-password"
									className={errors.password ? 'input-error pr-10' : 'input pr-10'}
									placeholder="请输入密码"
									{...register('password', {
										required: '请输入密码',
										minLength: {
											value: 8,
											message: '密码至少需要8个字符'
										},
										pattern: {
											value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
											message: '密码必须包含大小写字母和数字'
										}
									})}
								/>
								<button
									type="button"
									className="absolute inset-y-0 right-0 pr-3 flex items-center"
									onClick={() => setShowPassword(!showPassword)}
								>
									{showPassword ? (
										<EyeSlashIcon className="h-5 w-5 text-gray-400" />
									) : (
										<EyeIcon className="h-5 w-5 text-gray-400" />
									)}
								</button>
							</div>
							{errors.password && (
								<p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
							)}
						</div>

						<div>
							<label htmlFor="confirmPassword" className="label">
								确认密码
							</label>
							<div className="relative">
								<input
									id="confirmPassword"
									type={showConfirmPassword ? 'text' : 'password'}
									autoComplete="new-password"
									className={errors.confirmPassword ? 'input-error pr-10' : 'input pr-10'}
									placeholder="请再次输入密码"
									{...register('confirmPassword', {
										required: '请确认密码',
										validate: value =>
											value === password || '两次输入的密码不一致'
									})}
								/>
								<button
									type="button"
									className="absolute inset-y-0 right-0 pr-3 flex items-center"
									onClick={() => setShowConfirmPassword(!showConfirmPassword)}
								>
									{showConfirmPassword ? (
										<EyeSlashIcon className="h-5 w-5 text-gray-400" />
									) : (
										<EyeIcon className="h-5 w-5 text-gray-400" />
									)}
								</button>
							</div>
							{errors.confirmPassword && (
								<p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
							)}
						</div>
					</div>

					{/* 密码强度指示器 */}
					{password && (
						<div className="space-y-2">
							<div className="text-sm text-gray-600">密码强度：</div>
							<div className="flex space-x-1">
								{[1, 2, 3, 4].map((level) => {
									const strength = getPasswordStrength(password);
									return (
										<div
											key={level}
											className={`h-2 w-1/4 rounded ${
												level <= strength
													? strength <= 2
														? 'bg-red-500'
														: strength === 3
														? 'bg-yellow-500'
														: 'bg-green-500'
													: 'bg-gray-200'
											}`}
										/>
									);
								})}
							</div>
						</div>
					)}

					<div>
						<button
							type="submit"
							disabled={isLoading}
							className="btn-primary w-full flex justify-center py-3"
						>
							{isLoading ? (
								<>
									<div className="loading-spinner mr-2"></div>
									注册中...
								</>
							) : (
								'创建账户'
							)}
						</button>
					</div>

					<div className="text-center">
						<p className="text-sm text-gray-600">
							已有账户？{' '}
							<Link
								to="/login"
								className="font-medium text-primary-600 hover:text-primary-500"
							>
								立即登录
							</Link>
						</p>
					</div>
				</form>

				{/* 服务条款 */}
				<div className="mt-6 text-center">
					<p className="text-xs text-gray-500">
						注册即表示您同意我们的{' '}
						<a href="#" className="text-primary-600 hover:text-primary-500">
							服务条款
						</a>{' '}
						和{' '}
						<a href="#" className="text-primary-600 hover:text-primary-500">
							隐私政策
						</a>
					</p>
				</div>
			</div>
		</div>
	);
}

// 密码强度计算函数
function getPasswordStrength(password) {
	let strength = 0;
	
	if (password.length >= 8) strength++;
	if (/[a-z]/.test(password)) strength++;
	if (/[A-Z]/.test(password)) strength++;
	if (/\d/.test(password)) strength++;
	if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;
	
	return Math.min(strength, 4);
}

export default Register;
